<template>
  <div v-if="visible" class="login-modal-overlay" @click.self="close">
    <div :class="['login-modal', `mode-${activeTab}`]">
      <button class="close-btn" type="button" @click="close">×</button>
      <div class="tabs">
        <button :class="['tab', activeTab === 'login' ? 'active' : '']" type="button" @click="setTab('login')">
          Log In
        </button>
        <button :class="['tab', activeTab === 'signup' ? 'active' : '']" type="button" @click="setTab('signup')">
          Sign Up
        </button>
        <button :class="['tab', activeTab === 'forgot' ? 'active' : '']" type="button" @click="setTab('forgot')">
          Restore Password
        </button>
      </div>

      <div class="modal-body">
        <form v-if="activeTab === 'login'" class="login-form">
          <input type="email" class="form-input center-input" placeholder="E-Mail" v-model="loginForm.email">
          <input type="password" class="form-input center-input" placeholder="Password" v-model="loginForm.password">
          <label class="terms-check login-terms">
            <input type="checkbox">
            <span class="checkmark"></span>
            <span>I have read and accept the <router-link to="/terms?id=4" @click.native="close">Terms of
                Use</router-link></span>
          </label>
          <button class="submit-btn login-btn" type="button" :disabled="loading" @click="submitSuccess">Log In</button>
        </form>

        <form v-if="activeTab === 'signup'" class="wide-form">
          <div class="form-row">
            <label>E-Mail</label>
            <input type="email" class="form-input" placeholder="E-Mail" v-model="signupForm.email">
          </div>
          <div class="form-row">
            <label>Verification Code</label>
            <div class="code-field">
              <input type="text" class="form-input" placeholder="Verification Code" v-model="signupForm.code">
              <button type="button" :disabled="codeCooldown > 0" @click="sendCode('register')">{{ codeButtonText
              }}</button>
            </div>
          </div>
          <div class="form-row">
            <label>
              <span>Password</span>
              <em>At least 6 chars</em>
            </label>
            <input type="password" class="form-input" placeholder="Password" v-model="signupForm.password">
          </div>
          <div class="form-row">
            <label>Re-enter password</label>
            <input type="password" class="form-input" placeholder="Re-enter password"
              v-model="signupForm.confirmPassword">
          </div>
          <div class="form-row terms-row">
            <span></span>
            <label class="terms-check">
              <input type="checkbox" checked>
              <span class="checkmark"></span>
              <span>I have read and accept the <router-link to="/terms?id=4" @click.native="close">Terms of
                  Use</router-link></span>
            </label>
          </div>
          <div class="form-row action-row">
            <span></span>
            <button class="submit-btn compact-btn" type="button" :disabled="loading" @click="submitSuccess">Sign
              Up</button>
          </div>
        </form>

        <form v-if="activeTab === 'forgot'" class="wide-form forgot-form">
          <div class="form-row">
            <label>E-Mail</label>
            <input type="email" class="form-input" placeholder="E-Mail" v-model="forgotForm.email">
          </div>
          <div class="form-row">
            <label>Verification Code</label>
            <div class="code-field">
              <input type="text" class="form-input" placeholder="Verification Code" v-model="forgotForm.code">
              <button type="button" :disabled="codeCooldown > 0" @click="sendCode('forgot')">{{ codeButtonText
              }}</button>
            </div>
          </div>
          <div class="form-row">
            <label>
              <span>Password</span>
              <em>At least 6 chars</em>
            </label>
            <input type="password" class="form-input" placeholder="Re-enter password" v-model="forgotForm.password">
          </div>
          <div class="form-row">
            <label>Re-enter password</label>
            <input type="password" class="form-input" placeholder="Re-enter password"
              v-model="forgotForm.confirmPassword">
          </div>
          <div class="form-row action-row forgot-action">
            <span></span>
            <button class="submit-btn compact-btn" type="button" :disabled="loading"
              @click="submitSuccess">Submit</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { setToken } from '@/util/token-util.js'

export default {
  name: 'LoginModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    activeTab: {
      type: String,
      default: 'login'
    }
  },
  data() {
    return {
      loginForm: { email: '', password: '' },
      signupForm: { email: '', code: '', password: '', confirmPassword: '' },
      forgotForm: { email: '', code: '', password: '', confirmPassword: '' },
      loading: false,
      codeCooldown: 0,
      codeButtonText: 'Send verification Code',
      codeTimer: null
    }
  },
  beforeDestroy() {
    if (this.codeTimer) {
      clearInterval(this.codeTimer)
    }
  },
  methods: {
    setTab(tab) {
      this.$emit('update:activeTab', tab)
    },
    close() {
      this.$emit('close')
    },
    sendCode(formType) {
      const email = formType === 'register' ? this.signupForm.email : this.forgotForm.email
      if (!email) {
        this.$message.error('Please enter your email first')
        return
      }
      this.$api({
        url: 'User/sendCode',
        method: 'post',
        data: { email, type: formType }
      }).then(() => {
        this.$message.success('Verification code sent to your email')
        this.startCooldown()
      }).catch(() => {
        this.$message.error('Failed to send verification code')
      })
    },
    startCooldown() {
      this.codeCooldown = 60
      this.codeButtonText = 'Resend in 60s'
      this.codeTimer = setInterval(() => {
        this.codeCooldown--
        if (this.codeCooldown <= 0) {
          clearInterval(this.codeTimer)
          this.codeTimer = null
          this.codeButtonText = 'Send verification Code'
        } else {
          this.codeButtonText = `Resend in ${this.codeCooldown}s`
        }
      }, 1000)
    },
    submitSuccess() {
      if (this.loading) return
      if (this.activeTab === 'login') {
        this.handleLogin()
      } else if (this.activeTab === 'signup') {
        this.handleSignup()
      } else if (this.activeTab === 'forgot') {
        this.handleForgot()
      }
    },
    handleLogin() {
      const { email, password } = this.loginForm
      if (!email || !password) {
        this.$message.error('Please fill in all fields')
        return
      }
      this.loading = true
      this.$api({
        url: 'User/login',
        method: 'post',
        data: { email, password }
      }).then(res => {
        setToken(res.data.token)
        this.loginForm = { email: '', password: '' }
        this.$message.success('Login successful')
        this.$emit('success', res.data.user)
      }).catch(() => {
        this.$message.error('Login failed, please check your credentials')
      }).finally(() => {
        this.loading = false
      })
    },
    handleSignup() {
      const { email, code, password, confirmPassword } = this.signupForm
      if (!email || !code || !password || !confirmPassword) {
        this.$message.error('Please fill in all fields')
        return
      }
      if (password !== confirmPassword) {
        this.$message.error('Passwords do not match')
        return
      }
      this.loading = true
      this.$api({
        url: 'User/register',
        method: 'post',
        data: { email, password, password_confirmation: confirmPassword, code }
      }).then(res => {
        setToken(res.data.token)
        this.signupForm = { email: '', code: '', password: '', confirmPassword: '' }
        this.$message.success('Registration successful')
        this.$emit('success', res.data.user)
      }).catch(() => {
        this.$message.error('Registration failed')
      }).finally(() => {
        this.loading = false
      })
    },
    handleForgot() {
      const { email, code, password, confirmPassword } = this.forgotForm
      if (!email || !code || !password || !confirmPassword) {
        this.$message.error('Please fill in all fields')
        return
      }
      if (password !== confirmPassword) {
        this.$message.error('Passwords do not match')
        return
      }
      this.loading = true
      this.$api({
        url: 'User/resetPassword',
        method: 'post',
        data: {
          email,
          verification_code: code,
          password,
          confirm_password: confirmPassword
        }
      }).then(() => {
        this.forgotForm = { email: '', code: '', password: '', confirmPassword: '' }
        this.$message.success('Password reset successful, please log in')
        this.$emit('update:activeTab', 'login')
      }).catch(() => {
        this.$message.error('Password reset failed')
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.login-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.46);
}

.login-modal {
  width: 630px;
  position: relative;
  overflow: hidden;
  border-radius: 9px;
  background: #fff;
}

.mode-login {
  height: 400px;
}

.mode-signup {
  height: 584px;
}

.mode-forgot {
  height: 554px;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 17px;
  z-index: 2;
  width: 20px;
  height: 20px;
  border: 0;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 30px;
  font-weight: 300;
  line-height: 18px;
}

.tabs {
  height: 91px;
  padding-top: 46px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid #ddd;
  background: #fff;
}

.tab {
  height: 45px;
  border: 0;
  border-right: 1px solid #ddd;
  border-top: 1px solid #ddd;
  background: #fff;
  color: #666;
  cursor: pointer;
  font-size: 16px;
  line-height: 45px;
  text-align: center;
}

.tab:last-child {
  border-right: 0;
}

.tab.active {
  background: #f4f4f4;
  color: #F34B01;
  font-weight: 700;
}

.modal-body {
  height: calc(100% - 91px);
  background: #fff;
}

.login-form {
  width: 372px;
  margin: 50px auto 0;
}

.form-input {
  height: 44px;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  padding: 0 18px;
  color: #333;
  font-size: 16px;
  outline: none;
  background: #fff;
}

.form-input::placeholder {
  color: #999;
}

.form-input:focus {
  border-color: #F34B01;
}

.center-input {
  width: 372px;
  display: block;
  margin-bottom: 28px;
}

.login-terms {
  margin-top: 2px;
}

.terms-check {
  display: flex;
  align-items: center;
  color: #999;
  font-size: 16px;
  line-height: 20px;
  user-select: none;
}

.terms-check input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkmark {
  width: 20px;
  height: 20px;
  margin-right: 13px;
  background: url(../../assets/check0.png) no-repeat center/contain;
  position: relative;
  flex: 0 0 auto;
}

.terms-check input:checked+.checkmark {
  background-image: url(../../assets/check1.png);
}

.terms-check a {
  color: #F34B01;
  text-decoration: none;
}

.submit-btn {
  border: 0;
  border-radius: 6px;
  background: #F34B01;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
}

.submit-btn:hover {
  background: #f04700;
}

.login-btn {
  width: 372px;
  height: 44px;
  margin-top: 23px;
}

.wide-form {
  width: 540px;
  margin: 50px auto 0;
}

.form-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  column-gap: 22px;
  margin-bottom: 28px;
}

.form-row>label {
  color: #333;
  font-size: 16px;
  line-height: 20px;
  text-align: right;
}

.form-row>label em {
  display: block;
  margin-top: 2px;
  color: #aaa;
  font-style: normal;
  font-size: 14px;
}

.form-row>.form-input {
  width: 372px;
}

.code-field {
  width: 372px;
  height: 44px;
  display: flex;
  border: 1px solid #d4d4d4;
  border-radius: 6px;
  overflow: hidden;
}

.code-field .form-input {
  flex: 1;
  min-width: 0;
  height: 42px;
  border: 0;
  border-radius: 0;
}

.code-field button {
  width: 165px;
  border: 0;
  background: #fff;
  color: #F34B01;
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.terms-row {
  margin-top: -1px;
  margin-bottom: 27px;
}

.action-row {
  margin-bottom: 0;
}

.compact-btn {
  width: 162px;
  height: 44px;
}

.forgot-form {
  margin-top: 50px;
}

.forgot-action {
  margin-top: 38px;
}
</style>
